import React, { createContext, useCallback, useEffect, useState } from "react";
import { TnAOverviewsApiService } from "../../../services/tna-overviews/tna-overviews-api.service";
import { TnAOverviewDTO } from "../../../models/tna-overviews";
import TnAOverviews from "./TnAOverviews";
import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";
import { TnAOverviewsFilter } from "../../../models/tna-overviews/tna-overview.filter";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";

interface UpdatingEmployees {
  employeeIdsUpdating: string[];
}

export const UpdatingEmployeesContext = createContext<UpdatingEmployees>({
  employeeIdsUpdating: [],
});

interface State {
  employeeIdsUpdating: string[];
  loading: boolean;
  error: string;
  pendingRequests: string[];
  toggleToReloadTable: boolean;
}

const TnAOverviewsWrapper = () => {
  const navigate = useNavigate();

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    employeeIdsUpdating: [],
    pendingRequests: [],
    toggleToReloadTable: false,
  });

  const handleUpdate = useCallback(async (employeeIdsToUpdate: string[]) => {
    const requestId = uuidv4();

    setState((state) => ({ ...state, loading: true, pendingRequests: [...state.pendingRequests, requestId] }));

    try {
      const apiService = new TnAOverviewsApiService();
      apiService.useLoadItemsPath(`/UpdateTnAOverviews`);

      const response = await apiService.loadItems(
        { employeeIds: employeeIdsToUpdate } as TnAOverviewsFilter,
        undefined,
        [],
        {
          pageNumber: 0,
          pageSize: 100,
        },
        undefined,
        60,
        undefined
      );

      if (!response.error) {
        setState((state) => ({
          ...state,
          employeeIdsUpdating: state.employeeIdsUpdating.filter((id) => !employeeIdsToUpdate.includes(id)),
        }));
      } else if (response.error) {
        setState((state) => ({ ...state, error: response.error as string }));
      }

      toastifyResponse(response, "failed");
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({
        ...state,
        pendingRequests: state.pendingRequests.filter((id) => id !== requestId),
      }));
    }
  }, []);

  useEffect(() => {
    if (state.loading && !state.pendingRequests.length) {
      setState((state) => ({ ...state, loading: false, toggleToReloadTable: !state.toggleToReloadTable }));
    }
  }, [state.loading, state.pendingRequests]);

  const checkIfEmployeesNeedUpdate = useCallback(
    async (items: TnAOverviewDTO[], state: any) => {
      const employeesToUpdate = items
        .filter(
          (item) => !item.isUpToDate && item.tnAOk && !state.employeeIdsUpdating.includes(item.employee?.id as string)
        )
        .map((item) => item?.employee?.id as string);

      if (!!employeesToUpdate.length) {
        setState((state) => ({
          ...state,
          employeeIdsUpdating: [...state.employeeIdsUpdating, ...employeesToUpdate],
        }));

        await handleUpdate(employeesToUpdate);
      }
    },
    [state.employeeIdsUpdating]
  );

  const getItems = useCallback(
    (items: TnAOverviewDTO[]) => {
      checkIfEmployeesNeedUpdate(items, state);
    },
    [state.toggleToReloadTable]
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (state.loading) {
        const message = "Are you sure you want to leave? Your changes may not be saved.";
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [navigate, state.loading]);

  return (
    <UpdatingEmployeesContext.Provider
      value={{
        employeeIdsUpdating: state.employeeIdsUpdating,
      }}
    >
      <TnAOverviews getItems={getItems} />
    </UpdatingEmployeesContext.Provider>
  );
};

export default TnAOverviewsWrapper;
